import React, { useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Videocam as VideocamIcon,
} from "@mui/icons-material";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { MobileTimePicker } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { addMinutes, differenceInMinutes, format, formatISO } from "date-fns";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import { AppointmentKind, CreateAppointmentInput } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";

import CancelCreateAppointmentModal from "./CancelCreateAppointmentModal";
import SearchClientListView from "./SearchClientListView/SearchClientListView";

const drawerMarginCollapsed = -450;
const drawerMargin = 0;

const useStyles = makeStyles()((theme) => ({
  drawerContainer: {
    position: "fixed",
    top: 0,
    right: 0,
    marginTop: "60px",
    width: "450px",
    height: "100%",
    backgroundColor: colors.white,
    zIndex: theme.zIndex.drawer,
    overflowY: "auto",
    overflowX: "hidden",
  },

  drawerOpen: {
    marginRight: drawerMargin,
    transition: theme.transitions.create("margin-right", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  drawerClose: {
    marginRight: drawerMarginCollapsed,
    transition: theme.transitions.create("margin-right", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  drawerShadow: {
    // taken from Card component box-shadow style
    boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.2)",
  },

  form: {
    padding: "20px",
  },

  titleText: {
    fontSize: 24,
    fontWeight: 700,
    color: colors.dark,
    marginBottom: "20px",
  },

  closeButton: {
    color: "#26C9A7",
  },

  appointmentTypeIconContainer: {
    background: colors.white,
    padding: theme.spacing(2),
    borderRadius: 50,
    height: 40,
    width: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "10px",
  },

  iconBorder: {
    border: "1px solid #C8CED8",
  },

  appointmentIcon: {
    color: colors.gray,
  },

  appointmentText: {
    color: colors.gray,
  },

  appointmentTypeContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "15px",
    border: "1px solid #C8CED8",
    width: "160px",
    cursor: "pointer",
    marginRight: "20px",
    marginBottom: "20px",
  },

  onlineAppointment: {
    backgroundColor: colors.secondary,
    border: colors.secondary,
  },

  onlineAppointmentIcon: {
    color: colors.secondary,
  },

  inPersonAppointment: {
    backgroundColor: "#26C9A7",
    border: "#26C9A7",
  },

  inPersonAppointmentIcon: {
    color: "#26C9A7",
  },

  selectedAppointmentText: {
    color: colors.white,
  },

  appointmentTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: colors.dark,
    marginBottom: "12px",
  },

  fieldContainer: {
    marginBottom: "20px",
  },

  fieldLabel: {
    color: colors.gray,
    fontSize: "14px",
    marginBottom: "12px",
  },

  fields: {
    width: "100%",
  },
}));

const validationSchema = yup
  .object()
  .shape({
    clientId: yup.string().label("Client").required(),
    title: yup.string().label("Title").required(),
    date: yup.date().label("Date").required(),
    startTime: yup.date().label("Start time").required(),
    endTime: yup.date().label("End time").required(),
    appointmentType: yup
      .string()
      .label("AppointmentType")
      .required()
      .oneOf(Object.values(AppointmentKind)),
    details: yup.string().label("Details"),
  })
  .defined();

type FormValues = yup.InferType<typeof validationSchema>;

const StyledCancelButton = withStyles(Button, {
  root: {
    color: "#949BA8",
    border: "2px solid #949BA8",
    marginRight: 10,
    "&:hover": {
      color: "#949BA8",
      border: "2px solid #949BA8",
    },
  },
});

const FieldLabel = withStyles(Typography, {
  root: {
    color: colors.gray,
    fontSize: "14px",
    marginBottom: "12px",
    "& label": {
      color: "#D13F3F",
    },
  },
});

interface Props {
  open: boolean;
  onClose: () => void;
  handleCreate: (input: CreateAppointmentInput) => void;
}

const CreateAppointment: React.FC<Props> = ({
  open,
  onClose,
  handleCreate,
}) => {
  const { classes, cx } = useStyles();
  const [showCancel, setShowCancel] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [dateDiffInMinutes, setDateDiffInMinutes] = useState(30);

  const initialValues: FormValues = {
    clientId: "",
    title: "",
    date: new Date(),
    startTime: new Date(),
    endTime: addMinutes(new Date(), 30),
    appointmentType: AppointmentKind.Inperson,
    details: "",
  };
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful, isSubmitting },
    setValue,
    control,
    getValues,
    reset,
    watch,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    mode: "onBlur",
    reValidateMode: "onBlur",
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    reset(initialValues);
  }, [isSubmitSuccessful]);

  useEffect(() => {
    handleStartTimeOnChange();
  }, []);

  function handleStartTimeOnChange() {
    setValue(
      "endTime",
      addMinutes(getValues("startTime") as Date, dateDiffInMinutes)
    );
  }

  function handleEndTImeOnChange() {
    setDateDiffInMinutes(
      differenceInMinutes(
        getValues("endTime") as Date,
        getValues("startTime") as Date
      )
    );
  }

  function handleClose() {
    onClose();
    reset(initialValues);
  }

  const getAppointmentValue = () => watch("appointmentType");

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const formValues = validationSchema.cast(values);
    const input: CreateAppointmentInput = {
      clientId: formValues.clientId,
      attributes: {
        title: formValues.title,
        details: formValues.details,
        appointmentType: formValues.appointmentType,
        startDate: formatISO(formValues.date, { representation: "date" }),
        startTime: format(formValues.startTime, "HH:mm:ss"),
        endTime: format(formValues.endTime, "HH:mm:ss"),
      },
    };
    setHasSubmitted(true);
    handleCreate(input);
  };

  return (
    <div
      className={cx(classes.drawerContainer, [
        !open
          ? classes.drawerClose
          : [classes.drawerOpen, classes.drawerShadow],
      ])}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={classes.form}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Typography className={classes.titleText}>
              New Appointment
            </Typography>
            <IconButton
              className={classes.closeButton}
              size="small"
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box>
            <Typography className={classes.appointmentTitle}>
              Appointment Type
            </Typography>
          </Box>
          <Box display="flex">
            <Box
              className={cx(classes.appointmentTypeContainer, [
                getAppointmentValue() === AppointmentKind.Inperson
                  ? classes.inPersonAppointment
                  : "",
              ])}
              onClick={() => {
                setValue("appointmentType", AppointmentKind.Inperson);
              }}
            >
              <Box
                className={cx(classes.appointmentTypeIconContainer, [
                  getAppointmentValue() !== AppointmentKind.Inperson
                    ? classes.iconBorder
                    : "",
                ])}
              >
                <PersonIcon
                  className={cx(classes.appointmentIcon, [
                    getAppointmentValue() === AppointmentKind.Inperson
                      ? classes.inPersonAppointmentIcon
                      : "",
                  ])}
                />
              </Box>
              <Typography
                className={cx(classes.appointmentText, [
                  getAppointmentValue() === AppointmentKind.Inperson
                    ? classes.selectedAppointmentText
                    : "",
                ])}
              >
                In-person
              </Typography>
            </Box>
            <Box
              className={cx(classes.appointmentTypeContainer, [
                getAppointmentValue() === AppointmentKind.Online
                  ? classes.onlineAppointment
                  : "",
              ])}
              onClick={() => {
                setValue("appointmentType", AppointmentKind.Online);
              }}
            >
              <Box
                className={cx(classes.appointmentTypeIconContainer, [
                  getAppointmentValue() !== AppointmentKind.Online
                    ? classes.iconBorder
                    : "",
                ])}
              >
                <VideocamIcon
                  className={cx(classes.appointmentIcon, [
                    getAppointmentValue() === AppointmentKind.Online
                      ? classes.onlineAppointmentIcon
                      : "",
                  ])}
                />
              </Box>
              <Typography
                className={cx(classes.appointmentText, [
                  getAppointmentValue() === AppointmentKind.Online
                    ? classes.selectedAppointmentText
                    : "",
                ])}
              >
                Online
              </Typography>
            </Box>
          </Box>
          <Box className={classes.fieldContainer}>
            <FieldLabel>
              Appointment Title<label>*</label>
            </FieldLabel>
            <TextField
              className={classes.fields}
              type="text"
              placeholder="Enter appointment topic title"
              {...register(`title`)}
              error={!!errors.title?.message}
            />
            {errors.title && (
              <Typography
                variant="body1"
                color="error"
              >{`${errors.title?.message}`}</Typography>
            )}
          </Box>
          <Box className={classes.fieldContainer}>
            <FieldLabel>
              Book An Appointment With<label>*</label>
            </FieldLabel>
            <Controller
              control={control}
              name="clientId"
              render={({ field, fieldState }) => (
                <SearchClientListView
                  hasSubmitted={hasSubmitted}
                  useRadio
                  errorMessage={fieldState.error?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
            {errors.clientId?.message && (
              <Typography
                variant="body1"
                color="error"
              >{`${errors.clientId?.message}`}</Typography>
            )}
          </Box>
          <Box className={classes.fieldContainer}>
            <FieldLabel>
              Appointment Date<label>*</label>
            </FieldLabel>
            <Controller
              control={control}
              name="date"
              render={({
                field: { ref, name, onChange, ...field },
                fieldState,
              }) => (
                <DatePicker
                  {...field}
                  inputRef={ref}
                  onChange={(value) => onChange(value as Date)}
                  slotProps={{
                    textField: {
                      name,
                      fullWidth: true,
                      error: !!fieldState?.error,
                      helperText: fieldState?.error?.message,
                    },
                  }}
                />
              )}
            />
          </Box>
          <Box display="flex" style={{ margin: "0px -10px" }}>
            <Box
              className={classes.fieldContainer}
              style={{ padding: "0px 10px" }}
            >
              <FieldLabel>
                Start Time<label>*</label>
              </FieldLabel>
              <Controller
                control={control}
                name="startTime"
                render={({
                  field: { ref, onChange, ...field },
                  fieldState,
                }) => (
                  <MobileTimePicker
                    ref={ref}
                    onChange={(value) => onChange(value as Date)}
                    {...field}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!fieldState?.error,
                        helperText: fieldState?.error?.message,
                      },
                    }}
                  />
                )}
              />
            </Box>
            <Box
              className={classes.fieldContainer}
              style={{ padding: "0px 10px" }}
            >
              <FieldLabel>
                End Time<label>*</label>
              </FieldLabel>
              <Controller
                control={control}
                name="endTime"
                render={({
                  field: { ref, onChange, ...field },
                  fieldState,
                }) => (
                  <MobileTimePicker
                    ref={ref}
                    onChange={(value) => onChange(value as Date)}
                    {...field}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!fieldState?.error,
                        helperText: fieldState?.error?.message,
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
          <Box className={classes.fieldContainer}>
            <FieldLabel>Details</FieldLabel>
            <TextField
              className={classes.fields}
              type="text"
              placeholder="Share other details here"
              {...register(`details`)}
            />
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            marginBottom={20}
          >
            <StyledCancelButton
              variant="outlined"
              onClick={() => setShowCancel(true)}
            >
              Cancel
            </StyledCancelButton>

            {/* Disable button when form is not valid */}
            <Button color="primary" type="submit" disabled={isSubmitting}>
              Book Appointment
            </Button>
          </Box>
        </div>
      </form>
      <CancelCreateAppointmentModal
        visibility={showCancel}
        onClose={() => setShowCancel(false)}
        onCancel={handleClose}
      />
    </div>
  );
};

export default CreateAppointment;
